// These event names and payloads are the bundled Horizon standard-events contract.
const PRODUCT_SELECT = 'shopify:product:select';
const CART_UPDATE = 'shopify:cart:lines-update';
const CART_ERROR = 'shopify:cart:error';

function readJSON(element, fallback = null) {
  try { return JSON.parse(element?.textContent || 'null') ?? fallback; }
  catch { return fallback; }
}

class CurvilaNativePurchase extends HTMLElement {
  connectedCallback() {
    this.lifecycle?.abort();
    this.lifecycle = new AbortController();
    const { signal } = this.lifecycle;
    this.generation = 0;
    this.selecting = false;
    this.adding = false;
    this.failed = false;
    this.submitting = false;
    const route = new URL(window.location.href);
    this.explicitVariant = route.searchParams.has('variant') || route.searchParams.has('option_values');
    this.sizeChosen = this.explicitVariant;
    this.listen('click', this.onClick, signal);
    this.productScope.addEventListener('change', this.onChange, { signal, capture: true });
    this.listen('input', this.onInput, signal);
    this.listen('quantity-selector:update', this.refreshState, signal);
    this.productScope.addEventListener(PRODUCT_SELECT, this.onNativeSelection, { signal });
    this.listen(CART_UPDATE, this.onNativeCart, signal);
    this.listen(CART_ERROR, this.onCartError, signal);
    document.addEventListener('kaching-bundles-block-loaded', this.onBundleLoad, { signal });
    window.addEventListener('pageshow', this.onPageShow, { signal });
    Promise.all(['variant-picker','product-form-component','quantity-selector-component','add-to-cart-component'].map(name => customElements.whenDefined(name))).then(() => {
      if (signal.aborted) return;
      this.enableSectionRendering();
      this.applySizePrompt();
      this.syncQuantity();
      // A bundle app may claim this form at the document level before the theme adds a single item.
      window.addEventListener('submit', this.onSubmit, { signal });
      this.setAttribute('data-enhanced', '');
      this.refreshState();
      if (this.explicitVariant) this.announceSelection();
    });
  }

  disconnectedCallback() { this.lifecycle?.abort(); }

  listen(type, handler, signal, capture = false) {
    this.addEventListener(type, handler, { signal, capture });
  }

  get productScope() { return this.closest('[data-cvn-product]') || this; }
  get picker() { return this.productScope.querySelector('variant-picker.cvn-picker'); }
  get bundleWidget() { return this.productScope.querySelector('kaching-bundles-block:not([hidden])'); }
  get productForm() { return this.querySelector('product-form-component'); }
  get form() { return this.querySelector('form[data-cvn-form]'); }
  get record() { return this.querySelector('[data-cvn-record]'); }
  get sizeField() { return this.picker?.querySelector('[data-cvn-size]'); }
  get needsSize() { return Boolean(this.sizeField && !this.sizeChosen && this.dataset.productAvailable !== 'false'); }

  // Scope Horizon's HTML lookup to this PDP section. Combined listings still request the whole
  // linked product because Horizon intentionally replaces its main, not just its options.
  enableSectionRendering() {
    const picker = this.picker;
    if (!picker || picker.cvnSectionScoped || typeof picker.buildRequestUrl !== 'function') return;
    const original = picker.buildRequestUrl.bind(picker);
    picker.buildRequestUrl = (input, ...args) => {
      const url = new URL(original(input, ...args), window.location.href);
      const current = new URL(this.dataset.productUrl, window.location.href);
      const combined = input.dataset.connectedProductUrl && url.pathname !== current.pathname;
      if (!combined && url.origin === current.origin) url.searchParams.set('section_id', this.dataset.sectionId);
      return url.href;
    };
    picker.cvnSectionScoped = 'true';
  }

  // A shopper must explicitly choose a size when more than one exists. Keeping the server's
  // fallback option in an inaccessible radio lets Shopify resolve other axes before that choice.
  applySizePrompt() {
    const field = this.sizeField;
    if (!field || !this.needsSize) return;
    const checked = field.querySelector('input:checked:not([data-cvn-placeholder])');
    if (!checked) return;
    field.querySelector('[data-cvn-placeholder]')?.remove();
    const fallback = checked.cloneNode(false);
    fallback.setAttribute('hidden', '');
    fallback.setAttribute('aria-hidden', 'true');
    fallback.setAttribute('tabindex', '-1');
    fallback.removeAttribute('id');
    fallback.dataset.cvnPlaceholder = 'true';
    field.append(fallback);
    fallback.checked = true;
    const label = field.querySelector('[data-cvn-selected]');
    if (label) label.textContent = '';
  }

  getSelection() {
    const record = this.record?.dataset || {};
    const fields = this.picker?.querySelectorAll('fieldset[data-cvn-position]') || [];
    const options = [...fields].map(field => {
      const selected = field.querySelector('input:checked:not([data-cvn-placeholder])');
      return { position: Number(field.dataset.cvnPosition), name: field.dataset.cvnOptionName, value: selected?.value || '' };
    });
    const variant = this.needsSize || this.failed ? null : readJSON(this.picker?.querySelector('[data-cvn-variant]'));
    return {
      sectionId: this.dataset.sectionId, productId: this.dataset.productId,
      variant, variantId: variant ? String(variant.id) : null, options,
      featuredMediaId: this.failed ? null : record.mediaId || null,
      featuredImage: this.failed ? null : record.image || null,
    };
  }

  getState() {
    const record = this.record?.dataset || {};
    const selectedPlan = this.querySelector('[data-cvn-plan]')?.selectedOptions[0];
    const qty = Number(this.form?.elements.quantity?.value) || 1;
    const priceText = selectedPlan?.dataset.priceText || record.priceText || '';
    const available = record.canBuy === 'true' && !this.querySelector('[data-cvn-plan-unavailable]');
    const busy = Boolean(this.selecting || this.adding);
    const needsSize = this.needsSize;
    const label = needsSize ? 'Choose a size' : this.querySelector('[data-cvn-add-label]')?.textContent.trim() || this.dataset.label;
    return {
      sectionId:this.dataset.sectionId, productId:this.dataset.productId,
      canAdd: available && !busy && !this.failed && !needsSize, needsSize, busy, quantity:qty,
      priceText, totalText:qty === 1 ? priceText : this.subtotal(qty, selectedPlan), label,
      variantId:this.getSelection().variantId,
    };
  }

  subtotal(quantity, plan) {
    const record = this.record;
    if (!record?.dataset.id || this.failed) return '';
    const breaks = readJSON(record.querySelector('[data-cvn-breaks]'), []);
    let price = Number(plan?.dataset.price ?? record.dataset.price);
    if (!plan?.value) {
      const count = quantity + Number(record.dataset.cartCount || 0);
      for (const tier of breaks) if (count >= tier.min) price = Number(tier.price);
    }
    try {
      const formatter = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
        style:'currency', currency:this.dataset.currency, currencyDisplay:this.dataset.showCurrency === 'true' ? 'code' : 'symbol',
      });
      // Liquid appends hundredths for zero-subunit currencies such as JPY. Other currencies
      // are expressed in their currency's actual subunit, including three-decimal currencies.
      const scale = 10 ** Math.max(2, formatter.resolvedOptions().maximumFractionDigits);
      return formatter.format(price * quantity / scale);
    } catch {
      return quantity + ' × ' + (plan?.dataset.priceText || record.dataset.priceText || '');
    }
  }

  syncPlanPrice() {
    const plan = this.querySelector('[data-cvn-plan]')?.selectedOptions[0];
    const price = plan?.value ? plan.dataset.priceText : '';
    const section = this.closest('.shopify-section');
    for (const display of section?.querySelectorAll('product-price.cvn-price') || []) {
      if (display.dataset.productId !== this.dataset.productId) continue;
      const standard = display.querySelector('[ref="priceContainer"]');
      standard?.toggleAttribute('data-cvn-price-suppressed', Boolean(price));
      let value = display.querySelector('[data-cvn-plan-price]');
      if (!price) { value?.remove();continue; }
      if (!value) {
        value = document.createElement('span');
        value.className='cvn-plan-price';value.dataset.cvnPlanPrice='';
        display.append(value);
      }
      value.textContent=price;
    }
  }

  syncBundleVariant(widget, variantId) {
    if (widget && variantId && 'currentVariantId' in widget && String(widget.currentVariantId) !== variantId) {
      widget.currentVariantId = Number(variantId);
    }
  }

  onBundleLoad = (event) => {
    const widget = event.detail?.component;
    if (widget && widget === this.bundleWidget && !this.selecting) {
      this.syncBundleVariant(widget, this.getSelection().variantId);
    }
  };

  announceSelection() {
    const selection = this.getSelection();
    this.syncBundleVariant(this.bundleWidget, selection.variantId);
    this.dispatchEvent(new CustomEvent('cv-native:variant', { bubbles:true, detail:selection }));
  }

  refreshState = () => {
    if (!this.isConnected) return;
    const state = this.getState();
    this.syncPlanPrice();
    this.toggleAttribute('data-busy', state.busy);
    this.setAttribute('aria-busy', String(state.busy));
    const total = this.querySelector('[data-cvn-subtotal]');
    if (total) {
      total.hidden = state.quantity < 2 || !state.totalText;
      total.textContent = total.hidden ? '' : 'Item subtotal · ' + state.totalText;
    }
    this.dispatchEvent(new CustomEvent('cv-native:state', { bubbles:true, detail:state }));
  };

  showMessage(message, {retry=false,success=false} = {}) {
    const box = this.querySelector('[data-cvn-message]');
    if (!box) return;
    box.hidden = !message;
    box.toggleAttribute('data-success', success);
    box.replaceChildren();
    if (!message) return;
    box.append(document.createTextNode(message));
    if (retry || success) {
      box.append(' ');
      const control = document.createElement(success ? 'a' : 'button');
      if (success) { control.href = this.dataset.cartUrl; control.dataset.cvnOpenBag=''; }
      else { control.type='button'; control.dataset.cvnRetry=''; }
      control.textContent = success ? 'View bag' : 'Try again';
      box.append(control);
    }
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-cvn-retry]')) {
      const choice = this.picker?.querySelector('input:checked');
      if (choice) choice.dispatchEvent(new Event('change', {bubbles:true}));
    } else if (target?.closest('[data-cvn-open-bag]')) {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (this.dataset.cartMode === 'drawer' && typeof drawer?.open === 'function') {
        event.preventDefault();drawer.open();
      }
    }
  };

  onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.matches('[data-cvn-size] input:not([data-cvn-placeholder])') && this.picker?.contains(target)) {
      this.sizeChosen = true;
      this.sizeField?.querySelector('[data-cvn-placeholder]')?.remove();
      this.sizeField?.removeAttribute('data-cvn-invalid');
    }
    if (target.matches('[data-cvn-plan], [name="quantity"]') && this.form?.contains(target)) this.refreshState();
  };

  onInput = (event) => {
    if (event.target?.name === 'quantity') this.refreshState();
  };

  onNativeSelection = (event) => {
    if (event.target !== this.picker || !event.promise) return;
    const ticket = ++this.generation;
    this.selecting = true;
    this.failed = false;
    this.showMessage('');
    this.refreshState();
    this.settlement = this.finishSelection(event.promise, ticket);
  };

  async finishSelection(promise, ticket) {
    try {
      const { detail } = await promise;
      await Promise.resolve(); // Horizon has now morphed the form, price and the option markup.
      if (ticket !== this.generation || !this.isConnected) return;
      const next = detail?.html?.getElementById(this.id);
      const record = next?.querySelector('[data-cvn-record]');
      if (!record) throw Error('Missing section response');
      this.record?.replaceWith(record.cloneNode(true));
      this.replacePlans(next);
      this.applySizePrompt();
      this.syncQuantity();
      this.updateAddress();
    } catch (error) {
      if (ticket !== this.generation || !this.isConnected) return;
      this.failed = true;
      const idInput = this.form?.querySelector('[name="id"]');
      if (idInput) idInput.value = '';
      this.showMessage('We couldn’t update this selection.', {retry:true});
    } finally {
      if (ticket === this.generation && this.isConnected) {
        this.selecting = false;
        this.refreshState();
        this.announceSelection();
      }
    }
  }

  replacePlans(next) {
    const current = this.querySelector('[data-cvn-plans]');
    const updated = next.querySelector('[data-cvn-plans]');
    if (!current || !updated || current.isEqualNode(updated)) return;
    const prior = current.querySelector('select')?.value;
    const node = updated.cloneNode(true);
    current.replaceWith(node);
    const select = node.querySelector('select');
    if (prior !== undefined && select && [...select.options].some(o => o.value === prior)) select.value=prior;
  }

  syncQuantity() {
    const input = this.form?.elements.quantity;
    const record = this.record?.dataset;
    if (!input || !record) return;
    const ruleMax = record.ruleMax || '';
    const stock = record.maxStock === '' || record.maxStock === undefined ? null : Number(record.maxStock);
    const max = stock === null ? ruleMax : ruleMax ? Math.min(Number(ruleMax),stock) : stock;
    input.disabled = record.canBuy !== 'true';
    input.dataset.cartQuantity = record.cartCount || '0';
    const selector = input.closest('quantity-selector-component');
    selector?.updateConstraints?.(String(record.ruleMin || 1), max === '' || max == null ? null : String(max),String(record.ruleStep || 1));
    selector?.setCartQuantity?.(Number(record.cartCount || 0));
  }

  updateAddress() {
    if (window.Shopify?.designMode) return;
    const url = new URL(window.location.href);
    const id = this.record?.dataset.id;
    if (this.needsSize) {
      url.searchParams.delete('variant');
      url.searchParams.delete('option_values');
    } else if (id) {
      url.searchParams.set('variant',id);
      url.searchParams.delete('option_values');
    } else {
      url.searchParams.delete('variant');
      const values = [...this.picker.querySelectorAll('fieldset input:checked')].map(el=>el.dataset.optionValueId);
      if (values.length) url.searchParams.set('option_values',values.join(','));
    }
    if (url.href !== window.location.href) window.history.replaceState(window.history.state,'',url.href);
  }

  promptSize() {
    this.sizeField?.setAttribute('data-cvn-invalid','');
    this.showMessage('Choose a size to add this item to your bag.');
    const first = this.sizeField?.querySelector('label input:not([data-option-available="false"])') || this.sizeField?.querySelector('label input');
    first?.focus({preventScroll:true});
    this.sizeField?.scrollIntoView({block:'center',behavior:'instant'});
  }

  addFromSticky() {
    if (this.needsSize) { this.promptSize(); return; }
    if (this.adding) return;
    if (!this.enhancedFormReady()) this.scrollIntoView({block:'center'});
    else if (this.bundleWidget) this.form.querySelector('button[name="add"]')?.click();
    else this.form.requestSubmit();
  }

  enhancedFormReady() {return this.hasAttribute('data-enhanced') && this.form;}

  onSubmit = async (event) => {
    if (event.defaultPrevented || event.target !== this.form || !this.enhancedFormReady()) return;
    event.preventDefault();
    if (this.submitting || this.adding) return;
    if (this.needsSize) { this.promptSize(); return; }
    const ticket = this.generation;
    this.submitting = true;
    try {
      if (this.selecting) await this.settlement;
      if (!this.isConnected || this.generation !== ticket || this.failed) return;
      const record = this.record?.dataset;
      if (!record?.id || record.canBuy !== 'true' || this.form.elements.id.value !== record.id || this.querySelector('[data-cvn-plan-unavailable]')) {
        this.showMessage(this.dataset.unavailable || 'Unavailable');return;
      }
      if (!this.form.reportValidity()) return;
      const quantity = this.form.elements.quantity;
      const qty = Number(quantity?.value || 1);
      const amountInCart = Number(quantity?.dataset.cartQuantity || 0);
      if (!Number.isInteger(qty) || qty < Number(quantity?.min || 1) || (quantity?.max !== '' && qty + amountInCart > Number(quantity.max))) {
        this.showMessage('Choose an available quantity.');return;
      }
      this.showMessage('');
      this.productForm.handleSubmit(event);
    } finally { this.submitting=false; }
  };

  onNativeCart = (event) => {
    if (event.target !== this.productForm || event.action !== 'add' || !event.promise) return;
    this.adding=true;
    this.refreshState();
    const direct = this.dataset.directCheckout === 'true';
    if (direct) {
      // Preserve Horizon's analytics, live counts and drawer section updates. Only its automatic
      // opening for this checkout-bound event is temporarily suppressed, then restored immediately.
      const drawer = document.querySelector('theme-drawer#cart-drawer cart-drawer-component[auto-open]');
      if (drawer) {
        drawer.removeAttribute('auto-open');
        queueMicrotask(()=>{ if(drawer.isConnected) drawer.setAttribute('auto-open',''); });
      }
    }
    event.promise.then(({detail})=>{
      if (!this.isConnected || detail?.didError !== false) return;
      const redirect = direct ? this.dataset.checkoutUrl : this.dataset.cartMode === 'page' || !document.querySelector('theme-drawer#cart-drawer') ? this.dataset.cartUrl : '';
      if (redirect) {
        const link = new URL(redirect,window.location.origin);
        if (link.origin === window.location.origin) { this.leaving=true;window.location.assign(link.href); }
      } else {
        const selectedId = this.record?.dataset.id;
        if (selectedId && Array.isArray(detail.items)) {
          this.record.dataset.cartCount=String(detail.items.filter(item=>String(item.variant_id)===selectedId).reduce((sum,item)=>sum+Number(item.quantity),0));
          this.syncQuantity();
        }
        this.showMessage('Added to your bag.', {success:true});
      }
    }).catch(()=>{
      if (this.isConnected) this.showMessage('We couldn’t confirm the addition. Check your bag before trying again.');
    }).finally(()=>{
      if (!this.leaving) this.adding=false;
      this.refreshState();
    });
  };

  onCartError = (event) => {
    if (event.target !== this.productForm) return;
    const description = event.detail?.description;
    this.showMessage(typeof description === 'string' ? description : event.error || 'We couldn’t add this item.');
  };

  onPageShow = () => { this.leaving=false; this.adding=false; this.submitting=false;this.refreshState(); };
}

if (!customElements.get('cv-native-purchase')) customElements.define('cv-native-purchase', CurvilaNativePurchase);
